// ═══════════════════════════════════════════════════════
// useSwipeNavigation — Touch swipe to navigate between pages
// Detects horizontal swipes (left/right) on touch devices
// and navigates to adjacent pages in the nav order.
// ═══════════════════════════════════════════════════════
import { useRef, useEffect, useCallback } from "react";
import { flattenNav } from "./navConfig.js";

// ── Type Interfaces ──────────────────────────────────

interface TouchStartData {
  x: number;
  y: number;
  time: number;
}

const SWIPE_THRESHOLD: number = 80; // minimum px distance for a swipe
const SWIPE_VELOCITY: number = 0.3; // minimum px/ms
const MAX_Y_DELTA: number = 100; // ignore if vertical movement is too large

/**
 * Hook: attach to a container ref to enable swipe-left / swipe-right navigation.
 */
export function useSwipeNavigation(
  currentPage: string,
  setPage: (pageId: string) => void,
  enabled: boolean = true
): React.RefObject<HTMLDivElement | null> {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const touchStart = useRef<TouchStartData | null>(null);

  // Build ordered page list (excluding hidden pages)
  const getPageOrder = useCallback((): string[] => {
    return flattenNav()
      .filter((item) => !item.hidden)
      .map((item) => item.id);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent): void => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      touchStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
    };

    const onTouchEnd = (e: TouchEvent): void => {
      if (!touchStart.current) return;
      if (e.changedTouches.length !== 1) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStart.current.x;
      const dy = touch.clientY - touchStart.current.y;
      const dt = Date.now() - touchStart.current.time;

      touchStart.current = null;

      // Check if the swipe is horizontal enough
      if (Math.abs(dy) > MAX_Y_DELTA) return;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;

      // Check velocity
      const velocity = Math.abs(dx) / dt;
      if (velocity < SWIPE_VELOCITY) return;

      const pages = getPageOrder();
      const currentIndex = pages.indexOf(currentPage);
      if (currentIndex === -1) return;

      if (dx < 0 && currentIndex < pages.length - 1) {
        // Swipe left → next page
        setPage(pages[currentIndex + 1]);
      } else if (dx > 0 && currentIndex > 0) {
        // Swipe right → previous page
        setPage(pages[currentIndex - 1]);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled, currentPage, setPage, getPageOrder]);

  return containerRef;
}
